'use client';

import { useSyncExternalStore } from 'react';

import { cn } from '@ps/ui/utils';

import {
  bumpCommentCount,
  ensurePostStats,
  getPostStats,
  postStatsKey,
  subscribePostStats,
} from '../../functions/postStatsStore';
import { useOptimisticComments } from '../hooks/useOptimisticComments';
import { CommentInputBar } from './CommentInputBar';
import { CommentList } from './CommentList';

interface CommentsPanelProps {
  sellerId: string;
  postId: string;
  allowComments: boolean;
  /**
   * - `panel` (default) — self-contained column: heading bar, scrolling list,
   *   compose input. The host sizes it via `className` — `h-full` for a modal
   *   column, or responsive classes for the post detail (heightless so it flows
   *   in a shared scroll on mobile, a scrolling column on desktop).
   * - `inline` — list + heading only (no input, no own scroll), for a layout that
   *   renders the compose input separately.
   */
  variant?: 'panel' | 'inline';
  /**
   * Total count for the heading (bumped optimistically on create). Falls back to
   * the loaded-page count when omitted.
   */
  commentCount?: number;
  /** Called after a successful create so a parent can bump its own count. */
  onCommentCreated?: () => void;
  /** Merged onto the panel root — the host owns its layout / chrome. */
  className?: string;
  /** Merged onto the compose-input container (e.g. `sticky bottom-0` on mobile). */
  inputClassName?: string;
}

/**
 * The post comments panel — heading, root-comment list, and compose input, over a
 * single {@link useOptimisticComments} (list + input share optimistic state). The
 * host controls layout via `className`/`inputClassName`: the desktop modal column,
 * and the post detail (side-by-side on desktop, one shared scroll with a sticky
 * input on mobile) both use the `panel` variant with different classes; `inline`
 * drops the input for hosts that pin their own.
 */
export function CommentsPanel({
  sellerId,
  postId,
  allowComments,
  variant = 'panel',
  commentCount,
  onCommentCreated,
  className,
  inputClassName,
}: CommentsPanelProps) {
  // Share the count with the post footer via the reactive stats store, so an
  // optimistic create bumps both the heading here and the footer's comment count.
  const key = postStatsKey(sellerId, postId);
  ensurePostStats(key, {
    liked: false,
    likeCount: 0,
    commentCount: commentCount ?? 0,
  });
  const stats = useSyncExternalStore(
    listener => subscribePostStats(key, listener),
    () => getPostStats(key),
    () => getPostStats(key),
  );
  const { comments, isInitializing, hasMore, loadMore, submit, isSubmitting } =
    useOptimisticComments(sellerId, postId, () => {
      bumpCommentCount(key, 1);
      onCommentCreated?.();
    });

  const displayCount =
    commentCount != null
      ? (stats?.commentCount ?? commentCount)
      : comments.length;

  const commentList = (
    <CommentList
      comments={comments}
      isInitializing={isInitializing}
      hasMore={hasMore}
      onLoadMore={loadMore}
    />
  );

  if (variant === 'inline') {
    return (
      <div className={cn('flex flex-col gap-3 px-5 py-4', className)}>
        <h3 className="text-[15px] font-semibold text-black">
          Comments ({displayCount})
        </h3>
        {commentList}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-white text-black',
        className,
      )}
    >
      <div className="border-b border-black/10 px-5 py-4">
        <h3 className="text-[15px] font-semibold">Comments ({displayCount})</h3>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4">{commentList}</div>

      {allowComments ? (
        <CommentInputBar
          onSubmit={submit}
          submitting={isSubmitting}
          className={cn('border-t border-black/10 px-5 py-3', inputClassName)}
        />
      ) : null}
    </div>
  );
}
